import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import * as TanStackQueryProvider from "./integrations/tanstack-query/root-provider.tsx";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

import "./styles.css";
import { env } from "./config/env";

// Initialize Sentry before anything else
import { initializeSentry, Sentry } from "./config/sentry";
import reportWebVitals from "./reportWebVitals.ts";

initializeSentry();

import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { PostHogProvider } from "posthog-js/react";

import { initializeI18n } from "./lib/i18n-loader";
// Import our language detection and loading utilities
import { detectLocaleFromUrl } from "./lib/language-detection";
import { rampsActions } from "./stores/ramps-store";

// Create a new router instance
const TanStackQueryProviderContext = TanStackQueryProvider.getContext();

// Create the router with hot reload support
const router = createRouter({
	routeTree,
	context: {
		...TanStackQueryProviderContext,
	},
	defaultPreload: "intent",
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
});

// Handle hot reload in development
if (import.meta.hot) {
	// Accept updates to the route tree
	import.meta.hot.accept("./routeTree.gen", (newRouteTree) => {
		if (newRouteTree) {
			// Update the router with new route tree
			router.update({
				routeTree: newRouteTree.routeTree,
				context: {
					...TanStackQueryProviderContext,
				},
			});
		}
	});

	// Dispose of the router when hot reloading
	import.meta.hot.dispose(() => {
		router.cancelMatches();
	});
}

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

/**
 * Initialize and render the app with dynamic language loading
 */
async function initializeApp() {
	try {
		// Detect locale from URL parameters (or browser / default fallback).
		// Captures both the Lingui catalog code AND the original BCP-47 tag so
		// later API calls can round-trip the partner-supplied value verbatim.
		const detectedLocale = detectLocaleFromUrl();

		// Seed the store. URL-driven sources mark lang as authoritative so
		// /prepare and setContextData won't overwrite it later.
		rampsActions.setLocaleFromUrl({
			tag: detectedLocale.tag,
			source: detectedLocale.source,
		});

		// Initialize i18n with the detected Lingui catalog code.
		await initializeI18n(detectedLocale.lingui, true);

		// Render the app after language is loaded
		const rootElement = document.getElementById("app");
		if (rootElement && !rootElement.innerHTML) {
			const root = ReactDOM.createRoot(rootElement);
			root.render(
				<Sentry.ErrorBoundary
					fallback={({ error, resetError }) => (
						<div style={{ padding: "20px", textAlign: "center" }}>
							<h2>Something went wrong</h2>
							<p>
								{error instanceof Error
									? error.message
									: "An unknown error occurred"}
							</p>
							<button onClick={resetError} type="button">
								Try again
							</button>
						</div>
					)}
					showDialog
				>
					<PostHogProvider
						apiKey={env.POSTHOG_API_KEY}
						options={{
							api_host: env.POSTHOG_API_HOST,
							// Disable auto pageviews — we track manually via trackPageView()
							capture_pageview: false,
							// Enable page-leave events for session exit-point analysis
							capture_pageleave: true,
							// Only create person profiles for identified users (cost optimization)
							person_profiles: "identified_only",
							capture_exceptions: false,
							debug: false,
							disable_session_recording: false,
							loaded: () => {},
						}}
					>
						<I18nProvider i18n={i18n}>
							<TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
								<RouterProvider router={router} />
							</TanStackQueryProvider.Provider>
						</I18nProvider>
					</PostHogProvider>
				</Sentry.ErrorBoundary>,
			);
		}
	} catch (error) {
		Sentry.captureException(error, {
			tags: { component: "main", action: "initializeApp" },
		});
		console.error("Failed to initialize app:", error);

		// Fallback: render app with default English
		const rootElement = document.getElementById("app");
		if (rootElement) {
			const root = ReactDOM.createRoot(rootElement);
			root.render(
				<StrictMode>
					<div
						style={{
							padding: "20px",
							textAlign: "center",
							color: "red",
							fontFamily: "Arial, sans-serif",
						}}
					>
						<h2>Language Loading Error</h2>
						<p>Failed to load language resources. Please refresh the page.</p>
						<p>
							Error: {error instanceof Error ? error.message : "Unknown error"}
						</p>
					</div>
				</StrictMode>,
			);
		}
	}
}

// Initialize the app
initializeApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
